import { useState } from "react";
import { api } from "../api.ts";
import { useUndo } from "./UndoToast.tsx";
import { latePoints } from "../../shared/points.ts";
import { FREQ_LABEL } from "../../shared/schedule.ts";
import type { TaskView } from "../../shared/types.ts";
import { statusWord } from "./ui.tsx";

function ago(iso: string | null): string {
  if (!iso) return "never done";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "done today";
  if (days === 1) return "done yesterday";
  if (days < 14) return `done ${days} days ago`;
  if (days < 60) return `done ${Math.round(days / 7)} weeks ago`;
  return `done ${Math.round(days / 30)} months ago`;
}

/**
 * One job, one tap.
 *
 * The circle shows what is actually true, not what happened since the page
 * loaded: a satisfied job renders ticked, and tapping it takes the tick back.
 * Starting empty regardless of status meant an already-done job on a room page
 * looked undone, and tapping it filed a second completion.
 */
export function TaskRow({
  task,
  personId,
  onChanged,
  showRoom,
}: {
  task: TaskView;
  personId: number | null;
  onChanged: () => void;
  showRoom?: string;
}) {
  const [override, setOverride] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const undo = useUndo();

  const done = override ?? task.status === "ok";
  // A personal task is one kid's own copy; whoever taps it, the credit is
  // theirs — the card already says whose it is.
  const label = task.personName ? `${task.title} — ${task.personName}` : task.title;

  async function complete() {
    setBusy(true);
    setError(false);
    setOverride(true);
    try {
      const result = await api.complete(task.id, task.personId ?? personId);
      // The way back is the toast, for ten seconds, and then it stands. The
      // tick itself is not a toggle: a done job that still looked tappable is
      // exactly how the same job used to get counted twice.
      undo.offer({ completionId: result.completionId, what: label, who: task.personName });
      onChanged();
    } catch {
      setOverride(null);
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  const state = done
    ? override === true
      ? "done just now"
      : ago(task.lastDoneAt)
    : statusWord(task.status, task.daysLate, task.daysUntil);

  const meta = [
    showRoom,
    // The room page groups by frequency already; repeating it on every row
    // there, and on Today where it is irrelevant, just forces a second line.
    showRoom ? null : FREQ_LABEL[task.freq],
    state,
    task.lastDoneBy && done && override !== true ? `by ${task.lastDoneBy}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <li className={`tag tag--${done ? "ok" : task.status}`}>
      <button
        type="button"
        className="tag__tick"
        aria-pressed={done}
        aria-label={done ? `${label} is done` : `Mark ${label} done`}
        title={done ? "Done. A parent can take it back in the admin page." : undefined}
        disabled={busy || done}
        onClick={complete}
      >
        {done ? "✓" : ""}
      </button>
      <div className="tag__body">
        <div className="tag__title">
          {task.kidFriendly ? <span aria-label="a kid can do this">🧒 </span> : null}
          {label}
        </div>
        <div className="tag__meta">
          {meta}
          {error ? <span className="tag__error"> · didn’t save, try again</span> : null}
        </div>
      </div>
      {done ? null : (
        <span
          className={`points${latePoints(task.effortMin, task.daysLate) > 0 ? " points--bounty" : ""}`}
          title={task.daysLate > 0 ? `${task.daysLate} days late, so worth more` : "Points for this job"}
        >
          {task.pointsLabel}
        </span>
      )}
    </li>
  );
}
